/**
 * The run history for one company, fetched when somebody opens the tab.
 *
 * Deliberately not part of `useBrain`, for the reason `usePeers` next door is
 * not: that hook POLLS every three seconds while a run is in flight, and twelve
 * generations of measurements have nothing to do with the progress of the run
 * being polled.
 *
 * ⚠ BUT UNLIKE `usePeers`, "NOT LOADED YET" AND "NOTHING RECORDED" MUST NOT LOOK
 * THE SAME HERE, so this hook reports `loading` and the panel draws two different
 * sentences. The peer comparison can render as absent while it loads because
 * absence is its ordinary answer; an empty history is a CLAIM - "we have never
 * measured this site before" - and an operator who reads that during the first
 * two hundred milliseconds will go and start a run they did not need.
 *
 * A failure is reported rather than swallowed, for the same reason: there is a
 * difference between "no earlier run" and "we could not ask", and only one of
 * them means the site has never been looked at.
 *
 * `reloadKey` is how a finishing run gets into the list. The card knows the run's
 * generation and status; when either changes the history is asked again, which
 * covers the case of an operator watching this tab while a run lands.
 */
import { useEffect, useState } from "react";
import { getBrainHistory, type BrainHistory } from "../../../../lib/siteBrain";

export interface UseBrainHistory {
  history: BrainHistory | null;
  loading: boolean;
  /** True when the request failed. Not the same as an empty history. */
  failed: boolean;
}

export function useBrainHistory(domain: string, enabled: boolean, reloadKey: string): UseBrainHistory {
  const [history, setHistory] = useState<BrainHistory | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!enabled || !domain) {
      // Cleared rather than kept: the next company's tab must not open on the
      // previous company's runs, which is a mistake nobody would ever spot.
      setHistory(null);
      setLoading(false);
      setFailed(false);
      return;
    }

    let alive = true;
    setLoading(true);
    setFailed(false);
    getBrainHistory(domain)
      .then((res) => {
        if (!alive) return;
        setHistory(res);
        setLoading(false);
      })
      .catch(() => {
        if (!alive) return;
        setHistory(null);
        setFailed(true);
        setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [domain, enabled, reloadKey]);

  return { history, loading, failed };
}
